import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faChair, 
  faClock,
  faCheckCircle,
  faExclamationCircle,
  faUtensils
} from '@fortawesome/free-solid-svg-icons';

const FloorPlanContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const TimeSlider = styled.div`
  padding: 0.6rem 0.75rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
`;

const TimeDisplay = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #192f6a;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const SliderInput = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #dee2e6;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%);
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(25, 47, 106, 0.4);
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%);
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(25, 47, 106, 0.4);
    border: none;
  }
`;

const FloorPlanWrapper = styled.div`
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #dee2e6;
  border-radius: 12px;
  padding: 3rem;
  min-height: 800px;
  height: 800px;
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-template-rows: repeat(12, 1fr);
  gap: 0.6rem;
`;

const Table = styled.div`
  position: relative;
  background: ${props => {
    if (props.status === 'occupied') return 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
    if (props.status === 'reserved') return 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)';
    return 'linear-gradient(135deg, #28a745 0%, #218838 100%)';
  }};
  border: 3px solid ${props => {
    if (props.bookingType === 'coach') return '#fd7e14';
    if (props.bookingType === 'table') return '#17a2b8';
    if (props.bookingType === 'afternoon_tea') return '#28a745';
    return '#dee2e6';
  }};
  border-radius: 50%;
  aspect-ratio: 1;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: 600;
  box-shadow: 0 3px 8px rgba(0,0,0,0.15);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0,0,0,0.25);
  }
  
  grid-column: ${props => props.gridColumn || 'auto'};
  grid-row: ${props => props.gridRow || 'auto'};
`;

const TableNumber = styled.div`
  font-size: 1rem;
  margin-bottom: 0.25rem;
  line-height: 1.1;
`;

const TableCapacity = styled.div`
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  opacity: 0.95;
`;

const CustomerName = styled.div`
  font-size: 0.7rem;
  margin-top: 0.3rem;
  text-align: center;
  opacity: 0.9;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
`;

const BigTable = styled(Table)`
  grid-column: ${props => props.gridColumn};
  grid-row: ${props => props.gridRow};
  font-size: 1.3rem;
  padding: 1rem;
  border-radius: 50%;
  aspect-ratio: 1;
`;

const Legend = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
  border: 1px solid rgba(0,0,0,0.2);
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const StatCard = styled.div`
  background: ${props => props.background || 'white'};
  border: 2px solid ${props => props.borderColor || '#dee2e6'};
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.color || '#333'};
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  font-weight: 600;
  line-height: 1.2;
`;

// Normalize time to HH:MM for consistent comparison (handles "15:30:00", "15:30", "9:00:00")
const normalizeTime = (timeStr) => {
  if (!timeStr) return '00:00';
  const s = String(timeStr).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    const h = match[1].padStart(2, '0');
    const m = match[2];
    return `${h}:${m}`;
  }
  return '00:00';
};

// Normalize date to YYYY-MM-DD
const normalizeDate = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (s.includes('T')) return s.split('T')[0];
  return s.substring(0, 10);
};

// Generate time slots (every 30 minutes from 9:00 to 17:00)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 17; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 17) slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const FloorPlanView = ({ bookings, selectedDate: initialDate, onTableSelect }) => {
  const [selectedDate, setSelectedDate] = useState(() => initialDate ? normalizeDate(initialDate) : normalizeDate(new Date().toISOString()));
  const defaultTimeIndex = Math.min(6, TIME_SLOTS.length - 1); // 12:00
  const [currentTime, setCurrentTime] = useState(TIME_SLOTS[defaultTimeIndex] || '12:00');
  const [timeIndex, setTimeIndex] = useState(defaultTimeIndex);
  const [assignedTables, setAssignedTables] = useState({});
  
  // Update selected date when initial date changes
  useEffect(() => {
    if (initialDate) {
      setSelectedDate(normalizeDate(initialDate));
    }
  }, [initialDate]);

  // Define table layout with capacities (20x12 grid, proper spacing)
  const tables = [
    // Left wall - Small tables (2 seats) TR1-TR4
    { id: 'TR1', capacity: 2, gridColumn: '1 / 3', gridRow: '11 / 13', name: 'TR1' },
    { id: 'TR2', capacity: 2, gridColumn: '1 / 3', gridRow: '8 / 10', name: 'TR2' },
    { id: 'TR3', capacity: 2, gridColumn: '1 / 3', gridRow: '5 / 7', name: 'TR3' },
    { id: 'TR4', capacity: 2, gridColumn: '1 / 3', gridRow: '2 / 4', name: 'TR4' },
    
    // Top row - Small tables (2 seats) TR5-TR7
    { id: 'TR5', capacity: 2, gridColumn: '5 / 7', gridRow: '1 / 3', name: 'TR5' },
    { id: 'TR6', capacity: 2, gridColumn: '9 / 11', gridRow: '1 / 3', name: 'TR6' },
    { id: 'TR7', capacity: 2, gridColumn: '13 / 15', gridRow: '1 / 3', name: 'TR7' },
    
    // Right wall - Small tables (2 seats) TR8-TR9
    { id: 'TR8', capacity: 2, gridColumn: '19 / 21', gridRow: '2 / 4', name: 'TR8' },
    { id: 'TR9', capacity: 2, gridColumn: '19 / 21', gridRow: '5 / 7', name: 'TR9' },
    
    // Left-center area - Medium tables (4 seats) 15-17
    { id: '15', capacity: 4, gridColumn: '4 / 6', gridRow: '4 / 6', name: '15' },
    { id: '16', capacity: 4, gridColumn: '6 / 8', gridRow: '5 / 7', name: '16' },
    { id: '17', capacity: 4, gridColumn: '6 / 8', gridRow: '9 / 11', name: '17' },
    
    // Right-center area - Medium tables (4 seats) 14, 13, 12
    { id: '14', capacity: 4, gridColumn: '13 / 15', gridRow: '4 / 6', name: '14' },
    { id: '13', capacity: 4, gridColumn: '15 / 17', gridRow: '5 / 7', name: '13' },
    { id: '12', capacity: 4, gridColumn: '15 / 17', gridRow: '8 / 10', name: '12' },
    
    // Bottom area - Medium tables (4 seats) 11, 10
    { id: '11', capacity: 4, gridColumn: '11 / 13', gridRow: '9 / 11', name: '11' },
    { id: '10', capacity: 4, gridColumn: '14 / 16', gridRow: '10 / 12', name: '10' },
    
    // Big table in center (8 seats)
    { id: 'BIG', capacity: 8, gridColumn: '9 / 12', gridRow: '6 / 9', name: 'Big Table', isBig: true }
  ];

  // Auto-assign bookings to tables (or use manual table_id if set)
  useEffect(() => {
    if (!bookings || !selectedDate) return;

    const normDate = normalizeDate(selectedDate);
    const dayBookings = bookings.filter(b => normalizeDate(b.visit_date) === normDate);

    // Sort by time
    const sortedBookings = [...dayBookings].sort((a, b) => {
      const timeA = normalizeTime(a.visit_time);
      const timeB = normalizeTime(b.visit_time);
      return timeA.localeCompare(timeB);
    });

    const assigned = {};
    const tableAvailability = {};

    sortedBookings.forEach(booking => {
      const bookingTime = normalizeTime(booking.visit_time);
      
      // If booking has a manual table_id assignment, use that
      if (booking.table_id) {
        const manualTable = tables.find(t => t.id === booking.table_id);
        if (manualTable) {
          assigned[booking.id] = {
            tableId: manualTable.id,
            tableName: manualTable.name,
            time: bookingTime
          };
          const tableKey = `${manualTable.id}-${bookingTime}`;
          tableAvailability[tableKey] = true;
          return; // Skip auto-assignment
        }
      }
      
      // Auto-assign: find best table (smallest table that fits)
      const guests = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) 
                     || parseInt(booking.number_of_guests, 10) || 2;
      
      const suitableTables = tables
        .filter(t => t.capacity >= guests)
        .sort((a, b) => a.capacity - b.capacity);
      
      for (const table of suitableTables) {
        const tableKey = `${table.id}-${bookingTime}`;
        if (!tableAvailability[tableKey]) {
          assigned[booking.id] = {
            tableId: table.id,
            tableName: table.name,
            time: bookingTime
          };
          tableAvailability[tableKey] = true;
          break;
        }
      }
    });

    setAssignedTables(assigned);
  }, [bookings, selectedDate]);

  // Get bookings for current time slot (normalized date/time comparison)
  const getCurrentTimeBookings = () => {
    if (!bookings || !selectedDate) return [];
    const normDate = normalizeDate(selectedDate);
    const normCurrentTime = normalizeTime(currentTime);
    return bookings.filter(b => 
      normalizeDate(b.visit_date) === normDate && normalizeTime(b.visit_time) === normCurrentTime
    );
  };

  // Get table status and booking info
  const getTableInfo = (tableId) => {
    const currentBookings = getCurrentTimeBookings();
    const booking = currentBookings.find(b => assignedTables[b.id]?.tableId === tableId);
    
    if (booking) {
      return {
        status: 'occupied',
        booking: booking,
        bookingType: booking.booking_type || 'afternoon_tea'
      };
    }
    
    return { status: 'available', booking: null, bookingType: null };
  };

  // Calculate stats
  const stats = () => {
    const currentBookings = getCurrentTimeBookings();
    const totalTables = tables.length;
    const occupiedTables = currentBookings.filter(b => assignedTables[b.id]).length;
    const availableTables = totalTables - occupiedTables;
    const totalGuests = currentBookings.reduce((sum, b) => {
      return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) 
                    || parseInt(b.number_of_guests, 10) || 0);
    }, 0);
    
    return { totalTables, occupiedTables, availableTables, totalGuests };
  };

  const currentStats = stats();

  const handleTimeChange = (e) => {
    const index = parseInt(e.target.value, 10);
    setTimeIndex(index);
    setCurrentTime(TIME_SLOTS[index] || '12:00');
  };

  return (
    <FloorPlanContainer>
      <h2 style={{ 
        marginBottom: '1rem', 
        color: '#192f6a', 
        fontSize: '1.4rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <FontAwesomeIcon icon={faUtensils} />
        Floor Plan - Table Management
      </h2>

      {/* Date + Time Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ 
          padding: '0.75rem',
          background: 'linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%)',
          borderRadius: '8px',
          border: '2px solid #17a2b8'
        }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#192f6a', marginBottom: '0.35rem' }}>
            Select Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              width: '100%',
              padding: '0.4rem',
              border: '1px solid #17a2b8',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}
          />
        </div>
        <TimeSlider>
          <SliderHeader>
            <TimeDisplay>
              <FontAwesomeIcon icon={faClock} />
              {currentTime}
            </TimeDisplay>
            <div style={{ fontSize: '0.8rem', color: '#666' }}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </SliderHeader>
          <SliderInput
            type="range"
            min="0"
            max={TIME_SLOTS.length - 1}
            value={timeIndex}
            onChange={handleTimeChange}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.3rem', fontSize: '0.7rem', color: '#999' }}>
            <span>{TIME_SLOTS[0]}</span>
            <span>{TIME_SLOTS[TIME_SLOTS.length - 1]}</span>
          </div>
        </TimeSlider>
      </div>

      {/* Stats Bar */}
      <StatsBar>
        <StatCard background="#d4edda" borderColor="#28a745">
          <StatValue color="#28a745">{currentStats.availableTables}</StatValue>
          <StatLabel>Available</StatLabel>
        </StatCard>
        <StatCard background="#fff3cd" borderColor="#ffc107">
          <StatValue color="#856404">{currentStats.occupiedTables}</StatValue>
          <StatLabel>Occupied</StatLabel>
        </StatCard>
        <StatCard background="#d1ecf1" borderColor="#17a2b8">
          <StatValue color="#17a2b8">{currentStats.totalGuests}</StatValue>
          <StatLabel>Guests</StatLabel>
        </StatCard>
        <StatCard background="#e7f3ff" borderColor="#007bff">
          <StatValue color="#007bff">{currentStats.totalTables}</StatValue>
          <StatLabel>Total</StatLabel>
        </StatCard>
      </StatsBar>

      {/* Floor Plan */}
      <FloorPlanWrapper>
        {tables.map(table => {
          const info = getTableInfo(table.id);
          const Component = table.isBig ? BigTable : Table;
          
          return (
            <Component
              key={table.id}
              gridColumn={table.gridColumn}
              gridRow={table.gridRow}
              status={info.status}
              bookingType={info.bookingType}
              onClick={() => onTableSelect && onTableSelect({ table, booking: info.booking })}
              style={{ cursor: 'pointer' }}
            >
              <TableNumber>{table.name}</TableNumber>
              <TableCapacity>
                <FontAwesomeIcon icon={faChair} />
                {table.capacity} seats
              </TableCapacity>
              {info.booking && (
                <>
                  <CustomerName>{info.booking.customer_name}</CustomerName>
                  {info.booking.tearoom_staff_notes?.trim() && (
                    <div style={{ fontSize: '0.55rem', marginTop: '0.12rem', color: '#92400e', fontWeight: 700, lineHeight: 1.2 }}>
                      📋 Note
                    </div>
                  )}
                </>
              )}
              {!info.booking && (
                <div style={{ fontSize: '0.6rem', marginTop: '0.2rem', opacity: 0.8 }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.55rem' }} /> Free
                </div>
              )}
            </Component>
          );
        })}
        
      </FloorPlanWrapper>

      {/* Legend */}
      <Legend>
        <LegendItem>
          <LegendColor color="linear-gradient(135deg, #28a745 0%, #218838 100%)" />
          <span>Available</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="linear-gradient(135deg, #ffc107 0%, #e0a800 100%)" />
          <span>Reserved</span>
        </LegendItem>
        <LegendItem>
          <LegendColor color="linear-gradient(135deg, #dc3545 0%, #c82333 100%)" />
          <span>Occupied</span>
        </LegendItem>
        <div style={{ width: '2px', height: '16px', background: '#dee2e6', borderRadius: '1px' }} />
        <LegendItem>
          <div style={{ width: '20px', height: '4px', background: '#28a745', borderRadius: '2px' }} />
          <span>Afternoon Tea</span>
        </LegendItem>
        <LegendItem>
          <div style={{ width: '20px', height: '4px', background: '#17a2b8', borderRadius: '2px' }} />
          <span>Table</span>
        </LegendItem>
        <LegendItem>
          <div style={{ width: '20px', height: '4px', background: '#fd7e14', borderRadius: '2px' }} />
          <span>Coach</span>
        </LegendItem>
      </Legend>
    </FloorPlanContainer>
  );
};

export default FloorPlanView;
